import React, { Fragment, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HeadphonesOutlinedIcon from '@mui/icons-material/HeadphonesOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { formatDuration, SUPPORT_LEVELS } from '../calls/logCall';
import { RecordingPlayer } from '../calls/RecordingPlayer';
import { contactPath, crmReturnState } from '../crmPaths';
import {
  callerName,
  callOutcomeLabel,
  CallLogRow,
  describeFollowUp,
  describeHow,
  formatWhen,
  methodLabel,
  OutcomeTone,
  outcomeTone,
  personName,
} from './callLog';

const NONE = (
  <Box component="span" sx={{ color: '#cbd5e1' }}>
    —
  </Box>
);

/** Soft chip colours per outcome family, text and background. */
export const TONES: Record<OutcomeTone, { color: string; bg: string; border: string }> = {
  good: { color: '#15803d', bg: 'rgba(22, 163, 74, 0.1)', border: 'rgba(22, 163, 74, 0.3)' },
  retry: { color: '#b45309', bg: 'rgba(217, 119, 6, 0.1)', border: 'rgba(217, 119, 6, 0.3)' },
  bad: { color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.08)', border: 'rgba(220, 38, 38, 0.3)' },
  other: { color: '#1d4ed8', bg: 'rgba(37, 99, 235, 0.08)', border: 'rgba(37, 99, 235, 0.3)' },
  none: { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
};

export const OutcomeChip = ({ outcome }: { outcome: string }) => {
  const tone = TONES[outcomeTone(outcome)];
  return (
    <Chip
      size="small"
      label={callOutcomeLabel(outcome)}
      sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, color: tone.color, bgcolor: tone.bg, border: '1px solid', borderColor: tone.border }}
    />
  );
};

/** One line of the opened row: a label and its value. */
const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: 1, fontSize: '0.8125rem' }}>
    <Box component="span" sx={{ color: 'text.secondary', minWidth: 112 }}>
      {label}
    </Box>
    <Box component="span">{children}</Box>
  </Box>
);

const nameOf = (person: { firstName: string | null; lastName: string | null } | null) =>
  person ? [person.firstName, person.lastName].filter(Boolean).join(' ') : '';

interface CallLogTableProps {
  rows: Array<CallLogRow>;
  now: Dayjs;
  /** The admin asking, when the counts say who that is: their calls read "You". */
  myAdminId: number | null;
  stale: boolean;
  onCorrect: (call: CallLogRow) => void;
  /** Show only this person's calls. */
  onPerson: (contactId: number) => void;
}

const COLUMNS = ['', 'When', 'Person', 'Outcome', 'Caller', 'Call', 'Notes', 'Follow-up', ''];

/**
 * The calls, newest first. A row opens to show the whole of its notes and the
 * rest of the call; the person's name opens their page, whose way back returns
 * here as it was left.
 */
export const CallLogTable = ({ rows, now, myAdminId, stale, onCorrect, onPerson }: CallLogTableProps) => {
  const here = useLocation();
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());
  /** The call whose recording was asked for from its row: it plays as the row opens. */
  const [playing, setPlaying] = useState<number | null>(null);

  const toggle = (id: number) => {
    const next = new Set(open);
    if (next.has(id)) {
      next.delete(id);
      if (playing === id) setPlaying(null);
    } else next.add(id);
    setOpen(next);
  };

  const play = (id: number) => {
    setOpen((was) => new Set(was).add(id));
    setPlaying(id);
  };

  return (
    <TableContainer
      sx={{
        opacity: stale ? 0.55 : 1,
        transition: 'opacity 120ms',
        '& .MuiTableCell-root': { borderColor: '#eef1f5', px: 1.25 },
        '& .MuiTableCell-head': { color: 'text.secondary', fontSize: '0.75rem', letterSpacing: '0.02em', py: 1.25, fontWeight: 600, whiteSpace: 'nowrap' },
        '& .MuiTableCell-body': { py: 0.75, fontSize: '0.8125rem', whiteSpace: 'nowrap' },
        '& .MuiTableRow-hover:hover': { backgroundColor: '#f8fafc' },
      }}
    >
      <Table stickyHeader size="small" aria-label="Calls" aria-busy={stale}>
        <TableHead>
          <TableRow>
            {COLUMNS.map((label, index) => (
              <TableCell key={index} sx={index === 0 ? { width: 40, px: 1 } : undefined}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((call) => {
            const expanded = open.has(call.id);
            const name = personName(call.contact, call.contactId);
            const followUp = describeFollowUp(call, now, true);
            const caller = callerName(call, myAdminId);
            const support = SUPPORT_LEVELS.find((level) => level.value === call.supportLevel)?.label;
            return (
              <Fragment key={call.id}>
                <TableRow
                  hover
                  onClick={() => toggle(call.id)}
                  sx={{ cursor: 'pointer', '& > td': expanded ? { borderBottom: 0 } : {} }}
                  data-call={call.id}
                >
                  <TableCell sx={{ px: 1 }}>
                    <IconButton
                      size="small"
                      aria-label={`${expanded ? 'Close' : 'Open'} the call with ${name}`}
                      aria-expanded={expanded}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggle(call.id);
                      }}
                    >
                      {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    <Tooltip title={dayjs(call.occurredAt).format('dddd D MMMM YYYY, HH:mm')}>
                      <span>{formatWhen(call.occurredAt, now, true)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 140 }}>
                    <Link
                      component={RouterLink}
                      to={contactPath(call.contactId)}
                      state={crmReturnState(here)}
                      underline="hover"
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                      sx={{ fontWeight: 600, color: 'text.primary', '&:hover': { color: 'primary.main' } }}
                    >
                      {name}
                    </Link>
                    {call.contact?.doNotContact ? (
                      <Chip size="small" color="error" label="Do not contact" sx={{ ml: 1, height: 18, fontSize: '0.6875rem', fontWeight: 600 }} />
                    ) : call.contact?.doNotCall ? (
                      <Chip size="small" color="error" variant="outlined" label="Do not call" sx={{ ml: 1, height: 18, fontSize: '0.6875rem' }} />
                    ) : null}
                    {call.contact?.primaryPhone && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {call.contact.primaryPhone}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <OutcomeChip outcome={call.outcome} />
                    {call.setDoNotCall && (
                      <Typography variant="caption" color="error.main" component="div">
                        Asked not to be called
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{caller || NONE}</TableCell>
                  <TableCell>
                    <Tooltip title={describeHow(call) || 'Not recorded'}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                        {call.direction === 'INBOUND' && <CallReceivedIcon sx={{ fontSize: 14, color: 'text.secondary' }} aria-label="Inbound" />}
                        {call.direction === 'OUTBOUND' && <CallMadeIcon sx={{ fontSize: 14, color: 'text.secondary' }} aria-label="Outbound" />}
                        {[methodLabel(call.method), call.durationSec ? formatDuration(call.durationSec) : ''].filter(Boolean).join(' · ') || NONE}
                      </Box>
                    </Tooltip>
                    {call.recordingUrl && (
                      <Tooltip title="Play the recording">
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={`Play the recording of the call with ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            play(call.id);
                          }}
                          sx={{ ml: 0.5, my: -0.5 }}
                        >
                          <HeadphonesOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  {/* The one column that gives way: two lines at most, the rest when the row opens. */}
                  <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 180 }}>
                    {call.notes ? (
                      <Box
                        component="span"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.4 }}
                      >
                        {call.notes}
                      </Box>
                    ) : (
                      NONE
                    )}
                  </TableCell>
                  <TableCell sx={{ color: call.followUpDue ? 'error.main' : call.followUpDoneAt ? 'text.secondary' : undefined, fontWeight: call.followUpDue ? 600 : undefined }}>
                    {call.followUpAt ? (
                      <Tooltip title={`${call.followUpDoneAt ? 'Was due' : 'Due'} ${dayjs(call.followUpAt).format('dddd D MMMM YYYY, HH:mm')}`}>
                        <span>{followUp}</span>
                      </Tooltip>
                    ) : (
                      NONE
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ px: 1 }}>
                    {call.editable && (
                      <Tooltip title="Correct this call">
                        <IconButton
                          size="small"
                          aria-label={`Correct the call with ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onCorrect(call);
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ py: 0, ...(expanded ? {} : { borderBottom: 0 }) }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <Box
                        role="region"
                        aria-label={`The call with ${name}`}
                        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2, py: 1.5, pl: 6, pr: 2, whiteSpace: 'normal' }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                            Notes
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {call.notes || 'No notes.'}
                          </Typography>
                          {call.recordingUrl && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                                Recording
                              </Typography>
                              <RecordingPlayer url={call.recordingUrl} autoPlay={playing === call.id} />
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'grid', gap: 0.5, alignContent: 'start' }}>
                          <Fact label="When">{dayjs(call.occurredAt).format('dddd D MMMM YYYY, HH:mm')}</Fact>
                          {call.source === 'MANUAL' && dayjs(call.createdAt).diff(call.occurredAt, 'minute') > 5 && (
                            <Fact label="Logged">{dayjs(call.createdAt).format('D MMM YYYY, HH:mm')}</Fact>
                          )}
                          {call.source === 'MANUAL' && dayjs(call.updatedAt).diff(call.createdAt, 'second') > 2 && (
                            <Fact label="Corrected">{dayjs(call.updatedAt).format('D MMM YYYY, HH:mm')}</Fact>
                          )}
                          <Fact label="Recorded">{call.source === 'MANUAL' ? 'In this panel' : 'NationBuilder history'}</Fact>
                          {support && <Fact label="Support level">{support}</Fact>}
                          {call.followUpAt && (
                            <Fact label="Follow-up">
                              {formatWhen(call.followUpAt, now)}
                              {call.followUpDoneAt
                                ? `, done ${dayjs(call.followUpDoneAt).format('D MMM')}${call.followUpDoneBy ? ` by ${nameOf(call.followUpDoneBy)}` : ''}`
                                : call.followUpDue
                                  ? ', due'
                                  : ''}
                            </Fact>
                          )}
                          {call.contact?.mergedIntoId && <Fact label="Merged">This record was merged into #{call.contact.mergedIntoId}</Fact>}
                          <Box sx={{ mt: 0.5 }}>
                            <Link component="button" type="button" variant="body2" onClick={() => onPerson(call.contactId)}>
                              Only {name}'s calls
                            </Link>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
